// Helpers
import { actionCreator, getLocale } from '../../../helpers/utils';

import { PRODUCT_IDS } from '../../../helpers/enums';
import Resources from '../../../helpers/resources';
import { trackEvent, ANALYTICS_EVENTS } from '../../../helpers/analytics';
import toastActions from './toast-actions';

export const EDIT_GROUP_NAME = 'EDIT_GROUP_NAME';
export const UPDATE_GROUP_SELECTION = 'UPDATE_GROUP_SELECTION';
export const RESET_EDIT_INFORMATION = 'RESET_EDIT_INFORMATION';

// EDIT GROUP
export const GET_DEFAULT_COURSE = 'GET_DEFAULT_COURSE';
export const EDIT_GROUP_INFORMATION_START = 'EDIT_GROUP_INFORMATION_START';
export const EDIT_GROUP_INFORMATION_END = 'EDIT_GROUP_INFORMATION_END';

export const CREATE_MULTI_YEAR_GROUPS = 'CREATE_MULTI_YEAR_GROUPS';
export const UPDATE_MULTI_YEAR_GROUPS_CREATED_COUNT = 'UPDATE_MULTI_YEAR_GROUPS_CREATED_COUNT';
export const RESET_MULTI_YEAR_GROUPS_FETCH = 'RESET_MULTI_YEAR_GROUPS_FETCH';

export const DEFAULT_NEW_COURSE_ID = '00000000-0000-0000-0000-000000000000';
export const NO_UPF_COURSE_FOUND_ID = '0';

// Add Group
const addNewGroup = ({ classroomId, groupName, courseId, upfCourseId, newCourseId }) => async (dispatch) => {
    if (!Resources.addNewGroup) throw new Error('DeleteGroup resource is not available');

    try {
        const UPFCourseId = typeof upfCourseId === 'number' ? upfCourseId : -1;
        const newCourseID = newCourseId || DEFAULT_NEW_COURSE_ID;
        const addNewGroupResponse = await Resources.addNewGroup
            .post({ classroomId })
            .headers({ Locale: getLocale() })
            .withBody({})
            .withParams({
                name: groupName,
                courseId,
                upfCourseId: UPFCourseId,
                conquestCourseId: newCourseID,
                useUpfSchoolDefault: UPFCourseId === -1
            })
            .send();

        if (!addNewGroupResponse) {
            throw new Error('Network error: No response received from server');
        }

        if (!addNewGroupResponse.valid || (addNewGroupResponse.status >= 400 && addNewGroupResponse.status <= 599)) {
            const message =
                (addNewGroupResponse && addNewGroupResponse.data && addNewGroupResponse.data.message) ||
                (addNewGroupResponse && addNewGroupResponse.error && addNewGroupResponse.error.message) ||
                `Failed to create group. Status: ${addNewGroupResponse.status}`;

            const error = new Error(message);
            error.status = addNewGroupResponse.status;
            error.response = addNewGroupResponse;

            throw error;
        }

        dispatch(toastActions.showToast({
            variant: 'success',
            messageId: 'New Group Created',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.groupCreated);
        // Valid return
        return addNewGroupResponse;
    } catch (err) {
        dispatch(toastActions.showToast({
            variant: 'error',
            messageId: 'Error Updating Group',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        throw err;
    }
};

// Edit Group name
const editGroupName = ({ classroomId, groupId, groupName }) => async (dispatch) => {
    if (!Resources.editGroupCourses) {
        throw new Error('edit Group resource is not available');
    }

    try {
        const editGroupNameResponse = await Resources.editGroupCourses
            .put({ classroomId, groupId })
            .headers({ Locale: getLocale() })
            .withBody({
                name: groupName
            })
            // Prevent a .raw() chain method so that errors can be handled properly
            //      -> although the user will get a console error, it's a trade off we need
            .send();

        // Network failure / unexpected response
        if (!editGroupNameResponse) {
            throw new Error('Network error: No response received from server');
        }

        if (!editGroupNameResponse.valid || (editGroupNameResponse.status >= 400 && editGroupNameResponse.status <= 599)) {
            const message =
                (editGroupNameResponse && editGroupNameResponse.data && editGroupNameResponse.data.message) ||
                (editGroupNameResponse && editGroupNameResponse.error && editGroupNameResponse.error.message) ||
                `Failed to edit group. Status: ${editGroupNameResponse.status}`;

            const error = new Error(message);
            error.status = editGroupNameResponse.status;
            error.response = editGroupNameResponse;

            throw error;
        }

        const editGroupAction = actionCreator(EDIT_GROUP_NAME, {
            data: editGroupNameResponse.data
        });
        dispatch(editGroupAction);
        trackEvent(ANALYTICS_EVENTS.groupRenamed);

        return editGroupNameResponse;
    } catch (err) {
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        // Network error (offline, DNS, timeout, etc.)
        if (!navigator.onLine) {
            throw new Error('Network unavailable. Please check your internet connection.');
        }

        throw err;
    }
};

// Delete Group
const deleteExistingGroup = ({ classroomId, groupId }) => async (dispatch) => {
    if (!Resources.deleteGroup) {
        throw new Error('DeleteGroup resource is not available');
    }

    try {
        const deleteGroupResponse = await Resources.deleteGroup
            .delete({ classroomId, groupId })
            .headers({ Locale: getLocale() })
            .raw()
            .send();

        // Network failure / unexpected response
        if (!deleteGroupResponse) {
            throw new Error('Network error: No response received from server');
        }

        // HTTP error handling (4xx / 5xx)
        if (!deleteGroupResponse.valid || (deleteGroupResponse.status >= 400 && deleteGroupResponse.status <= 599)) {
            const message =
                (deleteGroupResponse && deleteGroupResponse.data && deleteGroupResponse.data.message) ||
                (deleteGroupResponse && deleteGroupResponse.error && deleteGroupResponse.error.message) ||
                `Failed to delete group. Status: ${deleteGroupResponse.status}`;

            const error = new Error(message);
            error.status = deleteGroupResponse.status;
            error.response = deleteGroupResponse;

            throw error;
        }

        dispatch(toastActions.showToast({
            variant: 'success',
            messageId: 'Group Deleted',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.groupDeleted);
        return deleteGroupResponse;
    } catch (err) {
        dispatch(toastActions.showToast({
            variant: 'error',
            messageId: 'Error Updating Group',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        throw err;
    }
};

// Edit Group Course
/**
 * prerequisite:
 * - requires to have information about the courseId, which is in courses-actions.js
 * @param classroomId
 * @param groupId
 * @param courseId
 * @param upfCourseId
 * @param newCourseId
 */
const editGroupCourse = ({ classroomId, groupId, courseId, upfCourseId, newCourseId }) => async (dispatch) => {
    if (!Resources.editGroupCourses) {
        throw new Error('EditGroup resource is not available');
    }

    try {
        const UPFCourseId = typeof upfCourseId === 'number' ? upfCourseId : 0;
        const newCourseID = newCourseId || DEFAULT_NEW_COURSE_ID;
        const editGroupCourseResponse = await Resources.editGroupCourses
            .put({ classroomId, groupId })
            .headers({ Locale: getLocale() })
            .withBody({
                courseId,
                upfCourseId: UPFCourseId,
                useUpfSchoolDefault: UPFCourseId === -1,
                conquestCourseId: newCourseID
            })
            .raw()
            .send();

        if (!editGroupCourseResponse.valid || (editGroupCourseResponse.status >= 400 && editGroupCourseResponse.status <= 599)) {
            const message =
                    (editGroupCourseResponse && editGroupCourseResponse.data && editGroupCourseResponse.data.message) ||
                    (editGroupCourseResponse && editGroupCourseResponse.error && editGroupCourseResponse.error.message) ||
                    `Failed to edit group. Status: ${editGroupCourseResponse.status}`;

            const error = new Error(message);
            error.status = editGroupCourseResponse.status;
            error.response = editGroupCourseResponse;

            throw error;
        }

        dispatch(toastActions.showToast({
            variant: 'success',
            messageId: 'Group Updated',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.groupEdited);
        // Valid return
        return editGroupCourseResponse;
    } catch (err) {
        dispatch(toastActions.showToast({
            variant: 'error',
            messageId: 'Error Updating Group',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        throw err;
    }
};

/** EDIT GROUP INFORMATION RETRIEVAL CODE
 * A list of requests trying to get information of the current groupd to get their course details
 * @param classroomId
 * @param groupId
 * @param productId
 */
const getEditGroupCourseInformation = ({
    classId,
    productId,
    groupId
}) => async (dispatch) => {
    /*
    Check if there's classroom groups for the current class,
    otherwise get the course details based on the group selected
     */ 
    if (!Resources.classroomGroups) {
        throw new Error('getEditGroupCourseInformation resource is not available');
    }

    try {
        const getGroupInformationStartAction = actionCreator(EDIT_GROUP_INFORMATION_START);
        await dispatch(getGroupInformationStartAction);

        const getGroupsResponse = await Resources.classroomGroups
            .get({
                classId,
                productId,
                groupId
            })
            .withParams({
                includeGlobalCourses: true
            })
            .headers({ Locale: getLocale() })
            .send();

        if (!getGroupsResponse.valid || (getGroupsResponse.status >= 400 && getGroupsResponse.status <= 599)) {
            const message =
                    (getGroupsResponse && getGroupsResponse.data && getGroupsResponse.data.message) ||
                    (getGroupsResponse && getGroupsResponse.error && getGroupsResponse.error.message) ||
                    `Failed to edit group. Status: ${getGroupsResponse.status}`;

            const error = new Error(message);
            error.status = getGroupsResponse.status;
            error.response = getGroupsResponse;

            throw error;
        }

        const {
            data: {
                countries, yearGroups, courses, groupUpfCourse, groupCourse, upfCourses,
                groupConquestCourse, conquestCourses, groupCountry, groupCourseYear
            }
        } = getGroupsResponse;

        const getCourseCountriesAction = actionCreator(GET_DEFAULT_COURSE, {
            countryList: countries,
            yearList: yearGroups,
            courseList: courses,
            groupUpfCourse,
            groupCourse,
            upfCourseList: productId === PRODUCT_IDS.Mathletics ? upfCourses : '',
            groupNewCourse: groupConquestCourse,
            newCourseList: conquestCourses,
            countryCode: groupCountry.code,
            year: groupCourseYear.yearGroupEnum
        });

        dispatch(getCourseCountriesAction);

        // Course data variables
        const getCourseData = getGroupsResponse.data;
        const courseId = getCourseData.groupCourse.id.toString();
        const yearGroup = getCourseData.groupCourseYear.yearGroupEnum.toString();
        const countryName = getCourseData.groupCountry.code.toString();
        const upfCourse = getCourseData.groupUpfCourse === null ? '-1' : getCourseData.groupUpfCourse.id.toString();
        const newCourse = getCourseData.groupConquestCourse === null ?
            DEFAULT_NEW_COURSE_ID : getCourseData.groupConquestCourse.id.toString();

        const isSchoolCourse = getCourseData.isSchoolCourse;
        const groupCountryCode = groupCountry.code;

        const courseInformation = {
            courseSelectValue: courseId,
            yearSelectValue: yearGroup,
            countrySelectValue: countryName,
            upfCourseSelectValue: upfCourse,
            newCourseSelectValue: newCourse
        };

        // eslint-disable-next-line max-len
        const {
            courseSelectValue,
            yearSelectValue,
            upfCourseSelectValue,
            newCourseSelectValue
        } = courseInformation;
        const countrySelectValue = isSchoolCourse ? '' : courseInformation.countrySelectValue;
        const groupUpfCourseOption = getCourseData.groupUpfCourse;
        const groupCourseOption = getCourseData.groupCourse;
        const groupNewCourseOption = getCourseData.groupConquestCourse;

        const getGroupInformationEndAction = actionCreator(EDIT_GROUP_INFORMATION_END, {
            data: {
                countrySelectValue,
                courseSelectValue,
                groupCourseOption,
                groupUpfCourseOption,
                upfCourseSelectValue,
                groupNewCourseOption,
                newCourseSelectValue,
                yearSelectValue,
                groupCountryCode
            }
        });
        await dispatch(getGroupInformationEndAction);

        return {
            isSchoolCoursesEnabled: isSchoolCourse,
            groupCountryCode
        };
    } catch (err) {
        dispatch(toastActions.showToast({
            variant: 'error',
            messageId: 'Error Updating Group',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        throw err;
    }
};

/**
 * A list of requests trying to get information of the current groupd to get their course details
 * @param defaultData
 */
const getDefaultCourseInformation = ({ defaultData }) => async (dispatch) => {
    /*
    Check if there's classroom groups for the current class,
    otherwise get the course details based on the group selected
     */
    if (!Resources.getDefaultCourse) {
        throw new Error('getEditGroupCourseInformation resource is not available');
    }

    try {
        const getGroupInformationStartAction = actionCreator(EDIT_GROUP_INFORMATION_START);
        await dispatch(getGroupInformationStartAction);

        let isSchoolCourse;
        let groupCountryCode;

        if (defaultData) {
            // To display rest of the data in select box if any one of the data(Country/Year/Course) is missing from BE
            const countryName = defaultData.data.defaultCountry.code !== null ?
                defaultData.data.defaultCountry.code : '';
            const yearGroup = defaultData.data.defaultYear !== null ?
                defaultData.data.defaultYear.yearGroupEnum.toString() : '';
            const courseId = defaultData.data.defaultCourse !== null ?
                defaultData.data.defaultCourse.id.toString() : '';
            const conquestId = defaultData.data.defaultConquestCourse !== null ?
                defaultData.data.defaultConquestCourse.id : DEFAULT_NEW_COURSE_ID;
            isSchoolCourse = defaultData.data.isSchoolCourse;
            groupCountryCode = defaultData.data.defaultCountry.code;

            const courseInformation = {
                courseSelectValue: courseId,
                yearSelectValue: yearGroup,
                countrySelectValue: countryName,
                newCourseSelectValue: conquestId
            };

                // Set default country, year and course values
            const { courseSelectValue, yearSelectValue, newCourseSelectValue } = courseInformation;
            const countrySelectValue = isSchoolCourse ? '' : courseInformation.countrySelectValue;

            const getGroupInformationEndAction = actionCreator(EDIT_GROUP_INFORMATION_END, {
                data: {
                    courseSelectValue,
                    yearSelectValue,
                    countrySelectValue,
                    upfCourseSelectValue: '-1',
                    newCourseSelectValue,
                    groupCountryCode
                }
            });
            await dispatch(getGroupInformationEndAction);
        }

        return {
            isSchoolCoursesEnabled: isSchoolCourse,
            groupCountryCode
        };
    } catch (err) {
        dispatch(toastActions.showToast({
            variant: 'error',
            messageId: 'Error Updating Group',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        throw err;
    }
};

const updateGroupCheckbox = ({ value, isSelected }) => async (dispatch) => {
    const updateGroupAction = actionCreator(UPDATE_GROUP_SELECTION, {
        value,
        isSelected
    });
    dispatch(updateGroupAction);
};

const updateStudentGroup = ({ newGroupId, userIds, classId }) => async (dispatch) => {
    if (!Resources.assignGroup) {
        throw new Error('getEditGroupCourseInformation resource is not available');
    }

    try {
        const changeGroupResponse = await Resources.assignGroup
            .patch({
                classId,
                groupId: newGroupId
            })
            .headers({ Locale: getLocale() })
            .withBody(userIds)
            .raw()
            .send();

        if (!changeGroupResponse.valid || (changeGroupResponse.status >= 400 && changeGroupResponse.status <= 599)) {
            const message =
                (changeGroupResponse && changeGroupResponse.data && changeGroupResponse.data.message) ||
                (changeGroupResponse && changeGroupResponse.error && changeGroupResponse.error.message) ||
                `Failed to edit group. Status: ${changeGroupResponse.status}`;

            const error = new Error(message);
            error.status = changeGroupResponse.status;
            error.response = changeGroupResponse;

            throw error;
        }

        const updateGroupAction = actionCreator('STUDENT_GROUP_CHANGED', {
            newGroupId,
            userIds
        });
        dispatch(updateGroupAction);
        trackEvent(ANALYTICS_EVENTS.studentsGroupChanged, {
            count: userIds ? userIds.length : 0
        });

        return changeGroupResponse.valid;
    } catch (err) {
        dispatch(toastActions.showToast({
            variant: 'error',
            messageId: 'Error Updating Group',
            autoCloseMs: 6000
        }));
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'groups' });
        throw err;
    }
};

const resetEditInformationHandler = () => async (dispatch) => {
    const updateGroupAction = actionCreator(RESET_EDIT_INFORMATION);
    dispatch(updateGroupAction);
};

const resetMultiYearGroupFetch = () => async (dispatch) => {
    const updateGroupAction = actionCreator(RESET_MULTI_YEAR_GROUPS_FETCH);
    dispatch(updateGroupAction);
};

const createMultiYearGroups = ({ classId }) => async (dispatch) => {
    if (Resources.createMultiYearGroups) {
        const actionGroup = actionCreator(CREATE_MULTI_YEAR_GROUPS);
        await dispatch(actionGroup);

        const createMultiYearGroupsResponse = await Resources.createMultiYearGroups
            .post({ classId })
            .headers({ Locale: getLocale() })
            .withBody({})
            .withParams({
                productId: PRODUCT_IDS.Mathletics
            })
            .send();

        const multiYearGroupCountAction = actionCreator(UPDATE_MULTI_YEAR_GROUPS_CREATED_COUNT, {
            data: {
                count: createMultiYearGroupsResponse.data.createdGroupsCount
            }
        });
        await dispatch(multiYearGroupCountAction);
        trackEvent(ANALYTICS_EVENTS.multiYearGroupsCreated, {
            count: createMultiYearGroupsResponse.data.createdGroupsCount
        });

        dispatch(toastActions.showToast({
            variant: 'success',
            messageId: 'One Roster - Toaster - Students have been rearranged - {numberOfGroups}',
            values: { numberOfGroups: createMultiYearGroupsResponse.data.createdGroupsCount },
            autoCloseMs: 6000
        }));
    }
};

export default {
    addNewGroup,
    editGroupName,
    deleteExistingGroup,
    editGroupCourse,
    getEditGroupCourseInformation,
    getDefaultCourseInformation,
    updateStudentGroup,
    updateGroupCheckbox,
    resetEditInformationHandler,
    createMultiYearGroups,
    resetMultiYearGroupFetch
};



// WEBPACK FOOTER //
// ./containers/console/actions/group-actions.js